import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Phone } from "lucide-react";
import SEO from "../components/SEO";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/site";

export default function Blog() {
    const [articles, setArticles] = useState([]);

    useEffect(() => {
        fetch(`${process.env.PUBLIC_URL}/articles/index.json`)
            .then((r) => r.json())
            .then(setArticles)
            .catch(() => setArticles([]));
    }, []);

    return (
        <div data-testid="blog-page">
            <SEO
                title="Blog Serrurerie Paris — Conseils, Tarifs & Sécurité | Serrurier Paris"
                description="Conseils d'artisans serruriers parisiens : éviter les arnaques, comprendre les tarifs d'ouverture de porte, sécuriser son logement après un cambriolage. ☎ 06 25 25 86 19"
                path="/blog"
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">Blog</span>
                    </nav>
                    <SectionLabel dark>Conseils d'artisans</SectionLabel>
                    <StatusBadge className="mt-6" />
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["Le blog du", "serrurier parisien."]} />
                    </h1>
                    <FadeUp delay={0.55}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">
                            Conseils de sécurité, transparence des tarifs, bons réflexes en urgence : nos artisans
                            partagent leur expérience du dépannage à Paris.
                        </p>
                    </FadeUp>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {articles.map((a, i) => (
                        <FadeUp key={a.slug} delay={(i % 3) * 0.08}>
                            <Link
                                to={`/blog/${a.slug}`}
                                data-testid={`blog-card-${i}`}
                                className="group flex h-full flex-col overflow-hidden rounded-xl border border-midnight/10 bg-white transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_28px_60px_-20px_rgba(10,17,40,0.35)]"
                            >
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img
                                        src={a.img}
                                        alt={a.title}
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="flex flex-1 flex-col p-7">
                                    <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-wider">
                                        <span className="text-emergency">{a.category}</span>
                                        <span className="text-midnight/35">{a.date}</span>
                                    </div>
                                    <h2 className="mt-3 font-display text-xl font-extrabold leading-snug tracking-tight">{a.title}</h2>
                                    <p className="mt-3 flex-1 text-sm leading-relaxed text-midnight/60">{a.excerpt}</p>
                                    <span className="mt-5 flex items-center justify-between text-xs font-bold uppercase tracking-[0.18em] text-emergency">
                                        <span className="flex items-center gap-2 text-midnight/40">
                                            <Clock className="h-3.5 w-3.5" /> {a.readTime}
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            Lire <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                        </span>
                                    </span>
                                </div>
                            </Link>
                        </FadeUp>
                    ))}
                </div>
            </section>

            <section className="grain bg-midnight py-16 text-white md:py-20">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Une urgence ne attend pas un article.
                        </h2>
                        <p className="mt-3 text-white/60">Artisan chez vous en 30 minutes, 24h/24. Devis gratuit au téléphone.</p>
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="blog-call-button"
                        className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                    >
                        <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                    </a>
                </div>
            </section>
        </div>
    );
}
